import Vue from 'vue'
import { addNamespace } from './utils'
import { getActivity } from '@/apis/activities'

/**
 * @file
 * @author Thomas Grinderslev <thomas@edulab.dk>
 */

/**
 * @typedef {{activityDetails: Object<Number, Object>, errors: Object<number, String>}} ActivityStoreState
 */

/**
 * The types used in this store
 * @enum {string}
 */
export const ActivityStoreTypes = {
  getters: {
    GET_ACTIVITY: 'getActivity',
    GET_ACTIVITY_ERROR: 'getActivityError',
  },
  actions: {
    FETCH_ACTIVITY: 'fetchActivity',
  },
  mutations: {
    CLEAR_ERROR: 'clearError',
    SET_ACTIVITY: 'setActivity',
    SET_ACTIVITY_ERROR: 'setActivityError',
  },
}

/**
 * A namespaced version of the types used in this store
 * @enum {string}
 */
export const ActivityStoreNamespacedTypes = addNamespace(
  'activities',
  ActivityStoreTypes
)

/**
 * @returns {ActivityStoreState}
 */
export function state() {
  return { activityDetails: {}, errors: {} }
}

export const getters = {
  /**
   * @param {ActivityStoreState} state
   * @returns {function(number):Object}
   */
  [ActivityStoreTypes.getters.GET_ACTIVITY]: state => id => {
    return state.activityDetails[id] || null
  },

  /**
   * @param {ActivityStoreState} state
   * @returns {function(number):String}
   */
  [ActivityStoreTypes.getters.GET_ACTIVITY_ERROR]: state => activityId => {
    return state.errors[activityId] || ''
  },
}

export const actions = {
  /**
   * @param {VuexCommit} commit
   * @param {VuexGetters} getters
   * @param {number} activityId
   */
  [ActivityStoreTypes.actions.FETCH_ACTIVITY]({ commit, getters }, activityId) {
    if (getters[ActivityStoreTypes.getters.GET_ACTIVITY](activityId) !== null) {
      return
    }

    commit(ActivityStoreTypes.mutations.CLEAR_ERROR, activityId)

    getActivity(activityId)
      .then(activityDetails => {
        commit(ActivityStoreTypes.mutations.SET_ACTIVITY, {
          activityId,
          activityDetails,
        })
      })
      .catch(({ message }) => {
        commit(ActivityStoreTypes.mutations.SET_ACTIVITY_ERROR, {
          activityId,
          error: message,
        })
      })
  },
}

export const mutations = {
  /**
   * @param {ActivityStoreState} state
   * @param {number} activityId
   */
  [ActivityStoreTypes.mutations.CLEAR_ERROR](state, activityId) {
    Vue.delete(state.errors, activityId)
  },

  /**
   * @param {ActivityStoreState} state
   * @param {number} activityId
   * @param {Object} activityDetails
   */
  [ActivityStoreTypes.mutations.SET_ACTIVITY](
    state,
    { activityId, activityDetails }
  ) {
    Vue.set(state.activityDetails, activityId, activityDetails)
  },

  /**
   * @param {ActivityStoreState} state
   * @param {Number} activityId
   * @param {Object} error
   */
  [ActivityStoreTypes.mutations.SET_ACTIVITY_ERROR](
    state,
    { activityId, error }
  ) {
    Vue.set(state.errors, activityId, error)
  },
}

export default {
  namespaced: true,
  Types: ActivityStoreTypes,
  NamespacedTypes: ActivityStoreNamespacedTypes,
  state,
  getters,
  actions,
  mutations,
}
